import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FiledownloadService } from '../service/filedownload.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { from, map, Observable, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { AppService } from 'app/app.service';
import { MapService } from 'app/features/flights/services/map.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MapProjectionService } from './service/map-projection.service';
import { MapData } from './model/mapData';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-map-projection',
    imports: [
        CommonModule
    ],
    templateUrl: './map-projection.component.html',
    styleUrl: './map-projection.component.scss'
})

export class MapProjectionComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  theme$?: string;
  subSettings?: Subscription;
  gLineByAircraft?: boolean = false;
  gLineSelectedColor?: string; // Default color if not set
  mapData$?: Observable<MapData[]>;
  toggleMarkersOff: boolean = false;

  constructor(
    private downloadService: FiledownloadService,
    private signalService: SignalsService,
    public appService: AppService,
    private cdr: ChangeDetectorRef,
    private mapService: MapService,
    private router: Router,
    private mapProjectionService: MapProjectionService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    //get the geoLineColor user selected from the route parameters
    const gLineByAircraft = this.route.snapshot.paramMap.get('gLineByAircraft');
    const gLineSelectedColor = this.route.snapshot.paramMap.get('gLineSelected');
    this.gLineByAircraft = JSON.parse(gLineByAircraft || 'false');
    this.gLineSelectedColor = gLineSelectedColor || 'blue'; // Default to blue if not provided
    
    // Initialize the map projection component
    this.initMapProjection().pipe(
      takeUntil(this.destroy$),
      tap((data) => {
        if (!data) {
          console.error('Map data is not available');
          return;
        }
        this.renderMap(data)
      })
    ).subscribe();

    this.subSettings = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
      this.cdr.markForCheck();
    });
  }

  initMapProjection(): Observable<MapData[]> {
    return this.signalService.employeeNumber$.pipe(
      takeUntil(this.destroy$),
      switchMap((empNum) => {
        if (!empNum) {
          console.error('Employee number is not available');
        }
        const fileName = `${empNum}.logbook_mp.json`;
        return from(this.downloadService.getContentofBlob(fileName)).pipe(
          map((blob) => {
            if (!blob) {
              console.error('Blob is not available');
            }
            const data = JSON.parse(blob);
            let id = 0;
            let mapData: MapData[] = [];
            data.forEach((leg: any) => {
              const exists = mapData.some(md =>
                md.depature.station === leg.departureStation &&
                md.arrival.station === leg.arrivalStation
              );
              if (!exists && !leg.isDeadhead && !leg.isCancelled) {
                mapData.push({
                  id: id++,
                  depature: {
                    station: leg.departureStation,
                    lat: leg.departureStation_Latitude,
                    long: leg.departureStation_Longitude
                  },
                  arrival: {
                    station: leg.arrivalStation,
                    lat: leg.arrivalStation_Latitude,
                    long: leg.arrivalStation_Longitude
                  },
                  aircraft: leg.aircraft_Short,
                  geoLineColor: this.gLineByAircraft ? 
                  this.mapProjectionService.getGeoLineColor(leg.aircraft_Short) : this.gLineSelectedColor
                });
              }
            });
            return mapData;
          })
        )
      })
    );
  }

  onResetMap(): void {
    this.mapService.initMap('projMap');
  }

  onBackButton(): void {
    this.router.navigate(['/export']);
  }


  //FIX ME - WIP: geoLineColor swtitching functionality
  // switchToDefaultColor(): void {
  //   this.gLineByAircraft = false;
  //   this.gLineSelectedColor = 'blue'; // Default color
  //   this.mapData$?.pipe(
  //     takeUntil(this.destroy$),
  //     tap((data) => {
  //       if (data) {
  //         console.log(this.gLineSelectedColor);
  //         data.forEach(point => {
  //           point.geoLineColor = this.gLineSelectedColor; // Set to default color
  //         });
  //         this.renderMap(data);
  //       } else {
  //         console.error('No map data available to switch color');
  //       }
  //     })
  //   ).subscribe();
  // }

  renderMap(mapData: MapData[]): void {
    this.mapService.renderMap('projMap', [], [], mapData, this.gLineByAircraft);
    this.mapService.showMarkers();
  }

  ngOnDestroy(): void {
    this.subSettings?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  markersToggle(): void {
    this.toggleMarkersOff = !this.toggleMarkersOff;
    if (this.toggleMarkersOff) {
      this.mapService.hideMarkers();
    }
    else {
      this.mapService.showMarkers(); 
    }
  }
}
